import { useContext, useMemo, useState } from "react";
import { AuthContext } from "src/utils/AuthContext";
import {
  Box,
  Button,
  Typography,
  IconButton,
  MenuList,
  MenuItem,
  ListItemIcon,
  Icon,
  Collapse,
  Divider,
  Drawer,
  Link,
  Switch,
  Chip,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import {
  BookOutlined,
  InfoOutlined,
  ExpandLess,
  ExpandMore,
  Add,
  RocketLaunchOutlined,
  WorkspacePremiumOutlined,
  LoginOutlined,
  LogoutOutlined,
  BugReportOutlined,
  DeviceHubOutlined,
  LightModeOutlined,
  UpgradeOutlined,
  HelpOutlineOutlined,
  SupportOutlined,
  CloseOutlined,
  AddCircle,
  WavingHandOutlined,
} from "@mui/icons-material";
import { handleCustomerPortal } from "src/utils/Utils";
import { getPageRoute } from "src/services";
import { PlanComparisonTable } from "src/components/upgrades/PlanComparisonTable.js";
import { customEvent } from "src/utils/gtag.js";
import CalendarExplainerTile from "src/components/explainers/CalendarExplainerTile.js";
import GoogleConnectDialog from "./dialogs/GoogleConnectDialog.js";

const Check = () => (
  <Icon
    baseClassName="material-symbols-outlined"
    sx={{ fontWeight: 300, fontSize: "1rem" }}
  >
    check
  </Icon>
);

export const SideMenu = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, handleSignOut } = useContext(AuthContext);
  const [openUpgrade, setOpenUpgrade] = useState(false);
  const [openBlog, setOpenBlog] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [showGoogleConnectDialog, setShowGoogleConnectDialog] = useState(false);

  const loggedIn = useMemo(() => {
    return !!user;
  }, [user]);

  const handleSignOutClick = async () => {
    await handleSignOut();
    navigate(getPageRoute("auth", "SIGNIN"));
  };

  const handleGoogleConnectToggle = (event) => {
    if (!user?.hasValidGoogleConnection) {
      // Navigate to Google Connect experience
      customEvent({
        name: "user_click_google_connect_toggle_on",
        category: "GoogleConnect",
        label: "Google Connect Toggle On",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
        },
      });
      if (user?.hasValidSubscription) {
        navigate(getPageRoute("help", "CONNECT_GOOGLE_CALENDAR"));
      } else {
        navigate(getPageRoute("help", "SUBSCRIBING_GOOGLE_CALENDAR_STEPS"))
      }
      onClose();
    } else {
      // Show dialog to email founders
      customEvent({
        name: "user_click_google_connect_toggle_off",
        category: "GoogleConnect",
        label: "Google Connect Toggle Off Attempt",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
        },
      });
      setShowGoogleConnectDialog(true);
    }
  };

  // used to determine if My Plan is shown
  const hasActiveSubscription = useMemo(() => {
    if (user)
      return !!user.hasValidSubscription || !!user.hasValidCreatorSubscription;
    return false;
  }, [user]);

  // used to determine if Upgrade menu should be shown
  const needsUpgrade = useMemo(() => {
    if (user)
      return !user.hasValidSubscription || !user.hasValidCreatorSubscription;
    return false;
  }, [user]);

  const isFreeUser = useMemo(() => {
    if (user)
      return (
        !user.hasValidSubscription &&
        !user.hasValidCreatorSubscription &&
        !user.enterpriseCreator
      );
    return false;
  }, [user]);

  const commonItems = {
    personalize: () => (
      <MenuItem
        component={RouterLink}
        to={getPageRoute("home", "PERSONALIZE")}
        onClick={onClose}
      >
        <ListItemIcon>
          <Icon
            baseClassName="material-symbols-outlined"
            fontSize="medium"
            sx={{ color: `${theme.palette.icon.primary}` }}
          >
            auto_fix_high
          </Icon>
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Personalize
        </Typography>
      </MenuItem>
    ),
    import: () => (
      <MenuItem component={RouterLink} to="/help/import" onClick={onClose}>
        <ListItemIcon>
          <Add sx={{ color: `${theme.palette.icon.primary}` }} />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Import
        </Typography>
      </MenuItem>
    ),
    about: () => (
      <MenuItem
        component={RouterLink}
        to="https://stanza-1.gitbook.io/stanza-help-center/stanza-overview/quickstart"
        target="_blank"
        onClick={onClose}
      >
        <ListItemIcon>
          <InfoOutlined
            fontSize="medium"
            sx={{ color: `${theme.palette.icon.primary}` }}
          />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          About
        </Typography>
      </MenuItem>
    ),
    vision: () => (
      <MenuItem component={RouterLink} to="/vision" onClick={onClose}>
        <ListItemIcon>
          <Icon
            baseClassName="material-symbols-outlined"
            fontSize="medium"
            sx={{ color: `${theme.palette.icon.primary}` }}
          >
            volunteer_activism
          </Icon>
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Giving Back
        </Typography>
      </MenuItem>
    ),
    upgrade: () => [
      <MenuItem
        component={RouterLink}
        onClick={() => setOpenUpgrade(!openUpgrade)}
        key="upgrade-menu-item"
      >
        <ListItemIcon>
          <UpgradeOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Upgrade
        </Typography>
        {openUpgrade ? (
          <ExpandLess sx={{ ml: "auto" }} />
        ) : (
          <ExpandMore sx={{ ml: "auto" }} />
        )}
      </MenuItem>,
      <Collapse
        in={openUpgrade}
        timeout="auto"
        unmountOnExit
        key="upgrade-collapse"
      >
        <Box>
          {user && !user.hasValidSubscription && (
            <MenuItem
              onClick={() => {
                onClose();
                navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                  state: { source: "nav_dropdown" },
                });
              }}
            >
              <ListItemIcon>
                <RocketLaunchOutlined
                  sx={{ color: `${theme.palette.icon.primary}` }}
                />
              </ListItemIcon>
              <Typography variant="h5" textAlign="center">
                Stanza+
              </Typography>
            </MenuItem>
          )}
          {user && !user.hasValidCreatorSubscription && (
            <MenuItem
              onClick={() => {
                onClose();
                navigate("/upgrade/creator");
              }}
            >
              <ListItemIcon>
                <Icon
                  baseClassName="material-symbols-outlined"
                  fontSize="medium"
                  sx={{ color: `${theme.palette.icon.primary}` }}
                >
                  editor_choice
                </Icon>
              </ListItemIcon>
              <Typography variant="h5" textAlign="center">
                Creator
              </Typography>
            </MenuItem>
          )}
        </Box>
      </Collapse>,
    ],
    support: () => [
      <MenuItem
        component={RouterLink}
        onClick={() => setOpenSupport(!openSupport)}
        key="support-menu-item"
      >
        <ListItemIcon>
          <SupportOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Support
        </Typography>
        {openSupport ? (
          <ExpandLess sx={{ ml: "auto" }} />
        ) : (
          <ExpandMore sx={{ ml: "auto" }} />
        )}
      </MenuItem>,
      <Collapse
        in={openSupport}
        timeout="auto"
        unmountOnExit
        key="support-collapse"
      >
        <Box>
          <MenuItem
            component={RouterLink}
            to="https://stanza-1.gitbook.io/stanza-help-center"
            target="_blank"
            data-testid="home-user-menu-help-center"
          >
            <ListItemIcon>
              <HelpOutlineOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Help Center
            </Typography>
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to={`https://docs.google.com/forms/d/e/${process.env.REACT_APP_SUPPORT_FORM_ID}/viewform`}
            target="_blank"
            data-testid="home-user-menu-report-issue"
          >
            <ListItemIcon>
              <BugReportOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Report
            </Typography>
          </MenuItem>
        </Box>
      </Collapse>,
    ],
    blog: () => [
      <MenuItem
        component={RouterLink}
        onClick={() => setOpenBlog(!openBlog)}
        key="blog-menu-item"
      >
        <ListItemIcon>
          <BookOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
        </ListItemIcon>
        <Typography variant="h5" textAlign="center">
          Blog
        </Typography>
        {openBlog ? (
          <ExpandLess sx={{ ml: "auto" }} />
        ) : (
          <ExpandMore sx={{ ml: "auto" }} />
        )}
      </MenuItem>,
      <Collapse in={openBlog} timeout="auto" unmountOnExit key="blog-collapse">
        <Box>
          <MenuItem
            component={RouterLink}
            to="https://stanzacal.ghost.io"
            target="_blank"
            onClick={onClose}
          >
            <ListItemIcon>
              <LightModeOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Spotlight
            </Typography>
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to="https://creatorhub.stanzacal.com"
            target="_blank"
            onClick={onClose}
            data-testid="home-user-creator-hub"
          >
            <ListItemIcon>
              <DeviceHubOutlined
                sx={{ color: `${theme.palette.icon.primary}` }}
              />
            </ListItemIcon>
            <Typography variant="h5" textAlign="center">
              Creator Hub
            </Typography>
          </MenuItem>
        </Box>
      </Collapse>,
    ],
  };

  const menuListSx = {
    "&>*": {
      px: "0px !important",
    },
    "&>hr": {
      my: 1,
    },
    "& .MuiCollapse-root": {
      mt: 0.5,
    },
    "&>:hover, & li:hover, & a:hover": {
      borderRadius: "4px",
    },
    "&>a:first-child, &>li:first-child": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
    "&>a:not(:first-child), &>li:not(:first-child)": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
    "&>div a:last-child, &>div li:last-child": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
    "&>div a:not(:last-child), &>div li:not(:last-child)": {
      pt: 0.5,
      pb: 0.5,
      minHeight: "auto",
    },
  };

  const renderMenu = () =>
    loggedIn ? (
      <MenuList sx={menuListSx}>
        {user?.provider === "google" && (
          <>
            <MenuItem onClick={handleGoogleConnectToggle}>
              <ListItemIcon>
                <Icon
                  baseClassName="material-symbols-outlined"
                  fontSize="medium"
                  sx={{ color: `${theme.palette.icon.primary}` }}
                >
                  sync
                </Icon>
              </ListItemIcon>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5" textAlign="center">
                  Google Connect
                </Typography>
                {!user?.hasValidGoogleConnection && (
                  <Chip
                    label="Recommended"
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{
                      height: "24px",
                      fontSize: "11px",
                      fontWeight: 500,
                      "& .MuiChip-label": {
                        px: 1,
                        fontSize: "11px",
                      },
                    }}
                  />
                )}
              </Box>
              <Switch
                checked={!!user?.hasValidGoogleConnection}
                sx={{ ml: "auto" }}
                size="small"
              />
            </MenuItem>
            <Divider />
          </>
        )}
        {user.handle &&
          [...(user.createdCalendars || []), ...(user.createdGroups || [])]
            .length > 0 && (
            <>
              <MenuItem
                component={RouterLink}
                to={`/${user.handle}`}
                onClick={onClose}
              >
                <ListItemIcon>
                  <Icon
                    baseClassName="material-symbols-outlined"
                    fontSize="medium"
                    sx={{ color: `${theme.palette.icon.primary}` }}
                  >
                    location_home
                  </Icon>
                </ListItemIcon>
                <Typography variant="h5" textAlign="center">
                  Your Profile
                </Typography>
              </MenuItem>
              <Divider />
            </>
          )}
        {hasActiveSubscription &&
          (user?.lifetimeMember ? (
            <>
              <MenuItem component={RouterLink} to="/upgrade/plus">
                <ListItemIcon>
                  <WorkspacePremiumOutlined
                    sx={{ color: `${theme.palette.icon.primary}` }}
                  />
                </ListItemIcon>
                <Typography variant="h5" textAlign="center">
                  Your Plan
                </Typography>
              </MenuItem>
              <Divider />
            </>
          ) : (
            <>
              <MenuItem onClick={handleCustomerPortal}>
                <ListItemIcon>
                  <WorkspacePremiumOutlined
                    sx={{ color: `${theme.palette.icon.primary}` }}
                  />
                </ListItemIcon>
                <Typography variant="h5" textAlign="center">
                  Your Plan
                </Typography>
              </MenuItem>
              <Divider />
            </>
          ))}
        {commonItems.import()}
        <Divider />
        <MenuItem component={RouterLink} to="/library" onClick={onClose}>
          <ListItemIcon>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{ color: `${theme.palette.icon.primary}` }}
            >
              newsstand
            </Icon>
          </ListItemIcon>
          <Typography variant="h5" textAlign="center">
            Library
          </Typography>
        </MenuItem>
        <Divider />
        {commonItems.personalize()}
        <Divider />
        {commonItems.about()}
        <Divider />
        {commonItems.vision()}
        <Divider />
        {needsUpgrade && commonItems.upgrade()}
        <Divider />
        {commonItems.blog()}
        <Divider />
        {commonItems.support()}
        <Divider />
        <MenuItem
          to="/signin"
          onClick={handleSignOutClick}
          data-testid="home-user-menu-sign-out"
        >
          <ListItemIcon>
            <LogoutOutlined color="delete" />
          </ListItemIcon>
          <Typography variant="h5" textAlign="center" color="delete.main">
            Sign Out
          </Typography>
        </MenuItem>
      </MenuList>
    ) : (
      <MenuList sx={menuListSx}>
        {commonItems.personalize()}
        <Divider />
        {commonItems.about()}
        <Divider />
        {commonItems.vision()}
        <Divider />
        {commonItems.blog()}
        <Divider />
        {commonItems.support()}
        <Divider />
        <MenuItem component={RouterLink} to="/signin" onClick={onClose}>
          <ListItemIcon>
            <LoginOutlined sx={{ color: `${theme.palette.icon.primary}` }} />
          </ListItemIcon>
          <Typography variant="h5" textAlign="center">
            Sign In
          </Typography>
        </MenuItem>
      </MenuList>
    );

  const handleExploreCalendars = () => {
    navigate(getPageRoute("home", "PERSONALIZE"));
    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          className: "thin-scrollbar",
          sx: {
            width: "342px",
            height: "100%",
            p: 2,
            boxShadow: theme.shadows[16],
            overflowY: "auto",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingBottom: "90px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {!loggedIn ? (
              <Link component={RouterLink} to={getPageRoute("auth", "SIGNIN")}>
                Sign into Account
              </Link>
            ) : (
              <Typography variant="body1">{user?.email}</Typography>
            )}
            <IconButton onClick={onClose}>
              <CloseOutlined fontSize="medium" />
            </IconButton>
          </Box>
          <Divider sx={{ mt: 1, mb: 4 }} />
          {!loggedIn ? (
            <>
              <Typography
                variant={"h2"}
                sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <WavingHandOutlined />
                Welcome to Stanza
              </Typography>
              <Typography variant={"body1"}>
                Where community and calendars meet. Subscribe to what you love
                or create your own calendars for others to follow.
              </Typography>
              <Divider sx={{ mt: 4 }} />
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant={"body2"} sx={{ mb: 0.5 }}>
                    Your Plan
                  </Typography>
                  <Typography variant={"h2"} sx={{ mb: 0.5 }}>
                    {user?.enterpriseCreator
                      ? "Stanza Enterprise"
                      : user?.hasValidCreatorSubscription
                      ? "Indie Creator"
                      : user?.hasValidSubscription &&
                        user?.hasValidCreatorSubscription
                      ? "Stanza+, Indie Cerator"
                      : user?.hasValidSubscription
                      ? "Stanza+"
                      : user?.hasValidCreatorSubscription
                      ? "Indie Creator"
                      : "Stanza Free"}
                  </Typography>
                </Box>
                {isFreeUser && (
                  <Button
                    variant="contained"
                    size="shrinked"
                    sx={{ height: "min-content", alignSelf: "flex-end" }}
                    onClick={() => {
                      navigate(getPageRoute("upgrade", "UPGRADE_PLUS"));
                      customEvent({
                        name: "user_click_side_menu_upgrade",
                        category: "Upgrade",
                        label: "SideMenu Upgrade Button (Logged In)",
                      });
                      onClose();
                    }}
                  >
                    UPGRADE
                  </Button>
                )}
              </Box>
              {!isFreeUser && (
                <>
                  <Button
                    variant="outlined"
                    size="shrinked"
                    as="a"
                    href={
                      user?.enterpriseCreator ||
                      user?.hasValidCreatorSubscription
                        ? "https://creatorhub.stanzacal.com/general-questions-678321613027fd001b426f91/"
                        : "https://spotlight.stanzacal.com/for-stanza-plus-members/"
                    }
                    target="_blank"
                    onClick={() => {
                      onClose();
                    }}
                    sx={{
                      my: 2,
                      textDecoration: "none",
                      textAlign: "center",
                    }}
                  >
                    See all benefits
                  </Button>
                </>
              )}
              <Divider sx={{ mt: 2 }} />
            </>
          )}
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            {renderMenu()}
            {!loggedIn && (
              <CalendarExplainerTile
                onButtonClick={handleExploreCalendars}
                buttonText="EXPLORE CALENDARS"
                location="side_menu"
                sx={{ mx: -2 }}
              />
            )}
          </Box>
        </Box>
      </Drawer>
      
      <GoogleConnectDialog
        open={showGoogleConnectDialog}
        onClose={() => setShowGoogleConnectDialog(false)}
        user={user}
      />
    </>
  );
};
